import { inject, Injectable } from '@angular/core';
import { Resource } from '@df/catalog/resource';
import { Client } from '@df/core/service/client';
import { ICmsContentService } from '@icms/core/content/icms-content.service';
import { ICmsModelResource } from '../model/icms-model.resource';
import { getBlockUrl } from './get-block-url';
import { ICmsBlock, NICmsBlock } from './icms-block';
import { TLoadParams } from '@df/catalog/model';
declare module '@df/catalog/resource' {
    interface IResources {
        block: Resource<ICmsBlock, number>;
    }
}
interface IRetrieveRequest {
    bareUrl: string;
    variant?: string;
}
@Injectable({
    providedIn: 'root'
})
export class ICmsBlockResource extends ICmsModelResource<ICmsBlock, number> {
    static override instance = ICmsBlock;
    readonly invalidatesUniversal = true;
    readonly invalidatesPrerender = true;
    private iCmsContentService = inject(ICmsContentService);

    constructor() {
        super();

        const autoloadRules = ['bootstrap'];
        if (this.app.client.isPrerender) {
            autoloadRules.push('prerender');
        }

        // when switching cms on reload all blocks to have verbosity 4 data
        this.app.client.isCms$.subscribe(value => {
            const v3ids = this.getAllExisting()
                .filter(block => block.data!.verbosity === 3)
                .map(i => i.id);
            if (v3ids.length) {
                this.loadAllIds(v3ids, 4);
            }
        });

        this.iCmsContentService
            .getComponentMetas()
            .filter(i => i.block?.autoload && autoloadRules.includes(i.block.autoload))
            .map(i => this.retrieve({ bareUrl: i.block!.url }, undefined));
    }

    retrieveExisting(request: IRetrieveRequest): ICmsBlock | undefined {
        return this.matchOne({ url: getBlockUrl(request.bareUrl, request.variant) });
    }

    async retrieve<TContent>(
        request: IRetrieveRequest & { initContent?: Partial<TContent> },
        params: Partial<TLoadParams> | undefined // keep it mandatory, its importatnt to pass params when possible mainly for loader
    ): Promise<ICmsBlock<NICmsBlock.IData<TContent>> | void> {
        const url = getBlockUrl(request.bareUrl, request.variant);

        try {
            const model = await this.findOne({ filter: { url }, verbosity: 3 }, params);
            if (!model) {
                throw new Error('Model not found');
            }
            return model as ICmsBlock<NICmsBlock.IData<TContent>>;
        } catch (err) {
            const model = this.getCreate(Math.min(0, ...this.getAll().map(i => i.id)) - 1);
            const content = request.initContent || {};
            const meta = this.iCmsContentService.getComponentMeta(request.bareUrl);
            if (meta) {
                this.iCmsContentService.initFieldset(meta.fields, content, content, meta.initialData);
            }

            // protection not to have multiste service error here if we do it before the app is ready
            if (!this.app.data) {
                await this.app.apiConfigService.loadApiConfig();
            }

            model.setData({
                url,
                verbosity: 3,
                type: 'block',
                id: model.id,
                content,
                store: this.app.multisite.current.id,
                name: url
            });
            if (meta?.block?.resolvers) {
                await model.load(model.verbosity);
            }
            return model;
        }
    }
}
